import {
	Button,
	ButtonGroup,
	Card,
	Heading,
	IconButton,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
	toast,
} from "@overmap-ai/blocks"
import {
	type Asset,
	type AssetTypeFieldValuesAttachment,
	type AssetTypeIdentifier,
	type AssetTypeIdentifierValue,
	type AssetTypeStatus,
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
	type Issue,
	type IssueType,
	pluralize,
	type ProcedureInitiative,
	type ProcedureType,
	type Stored,
	submittedAtModelSortFn,
	toFileNameSafeString,
} from "@overmap-ai/common"
import {
	APIError,
	selectAssetTypeById,
	selectAssetTypeIdentifiersOfAssetType,
	selectAssetTypeStatusesOfAssetType,
	selectIssuesOfProject,
	selectIssueTypesOfOrganization,
	selectLatestAssetTypeFieldsOfAssetType,
	selectProcedureTypesOfOrganization,
} from "@overmap-ai/core"
import type { Field, FieldValues } from "@overmap-ai/forms"
import { useOpenState, useSort } from "@overmap-ai/hooks"
import { getRouteApi, Link, useLocation, useNavigate } from "@tanstack/react-router"
import type { SortingState, Updater, VisibilityState } from "@tanstack/react-table"
import { cx } from "class-variance-authority"
import { saveAs } from "file-saver"
import Papa from "papaparse"
import { type ChangeEventHandler, memo, useCallback, useMemo } from "react"
import { AssetDataFilterMenu } from "src/components/FilterMenus/AssetDataFilterMenu"

import { ProcedureInitiativeCard } from "../../components/Cards/ProcedureInitiativeCard"
import { IssueCommandMenu } from "../../components/CommandMenus/IssueCommandMenu"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { AssetPdfExportOptionsDialog } from "../../components/Dialogs/AssetPdfExportOptionsDialog"
import { AssetBulkDropdownMenu } from "../../components/DropdownMenus/AssetBulkDropdownMenu"
import { AssetTypeDropdownMenu } from "../../components/DropdownMenus/AssetTypeDropdownMenu"
import { useDeserializeFieldSchema } from "../../components/Form/hooks"
import { getFieldValuesAttachmentsByFieldId } from "../../components/Form/utils"
import { AssetTypeIcon } from "../../components/Icons/AssetTypeIcon"
import { useAssetTable, useAssetTableColumns } from "../../components/Tables"
import { ColumnVisibilityDropdown, PaginationControl } from "../../components/Tables/components"
import { defaultRowsPerPageOptions } from "../../components/Tables/constants"
import { tableIconMapping } from "../../components/Tables/mappings"
import { Table } from "../../components/Tables/Table"
import { loadingToast } from "../../components/Toasts"
import { getAssetDataCsvExporter } from "../../csv/CSVExporter/assets"
import { CSVImporter } from "../../csv/CSVImporter/CSVImporter"
import { getCSVImportColumnDefsOfFieldSchema, getCSVImportColumnDefsOfIdentifiers } from "../../csv/CSVImporter/utils"
import { interactiveCardCva } from "../../cva"
import { FilterRenderer } from "../../filtering/components"
import type { AnyFilter } from "../../filtering/filter"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { useAssetDataFilterSchema } from "../../filtering/schemas"
import { useAssetData } from "../../hooks/assets"
import { useIssuesWithIdentifiers } from "../../hooks/issues"
import { useConfirmDeleteDialog, useFileInputRef } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import {
	setAssetTypeColumnSorting,
	setAssetTypeColumnVisibility,
	setAssetTypeFilters,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../store"
import {
	selectAssetsOfProjectAndAssetType,
	selectAssetTypeFiltersById,
	selectAttachmentsByAssetTypeFieldValues,
	selectColumnSortingOfAssetType,
	selectColumnVisibilityOfAssetType,
	selectProcedureInitiativesOfProjectAndAssetType,
} from "../../store/selectors"
import type { AssetData } from "../../typings"
import type { AssetPdfExportOptions } from "../../typings/reports"
import { uuid } from "../../utils/string"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/asset-types/$assetTypeId/")

export const AssetTypeDetailView = memo(() => {
	const { assetTypeId, projectId, organizationId } = routeApi.useParams()
	const assetType = useAppSelector(selectAssetTypeById(assetTypeId))

	if (!assetType) {
		throw new Error(`expected asset type to be defined, got ${assetType}`)
	}

	const navigate = useNavigate()
	const location = useLocation()
	const dispatch = useAppDispatch()
	const latestAssetTypeFields = useAppSelector(selectLatestAssetTypeFieldsOfAssetType(assetType.uuid))
	const assets = useShallowEqaulSelector(selectAssetsOfProjectAndAssetType(projectId, assetType.uuid))
	const assetData = useAssetData(assets)
	const serializedAssetDataFilters = useAppSelector(selectAssetTypeFiltersById(assetType.uuid))
	const columnSorting = useAppSelector(selectColumnSortingOfAssetType(assetType.uuid))
	const columnVisibility = useAppSelector(selectColumnVisibilityOfAssetType(assetType.uuid))
	const procedureTypes = useShallowEqaulSelector(selectProcedureTypesOfOrganization(organizationId))
	const procedureInitiatives = useShallowEqaulSelector(
		selectProcedureInitiativesOfProjectAndAssetType(projectId, assetType.uuid),
	)
	const assetTypeIdentifiers = useShallowEqaulSelector(selectAssetTypeIdentifiersOfAssetType(assetType.uuid))
	const assetTypeStatuses = useShallowEqaulSelector(selectAssetTypeStatusesOfAssetType(assetType.uuid))
	const issueTypes = useShallowEqaulSelector(selectIssueTypesOfOrganization(organizationId))
	const issues = useShallowEqaulSelector(selectIssuesOfProject(projectId))
	const issuesAndIdentifiers = useIssuesWithIdentifiers(issues)
	const attachmentsByAssetTypeFieldValues = useAppSelector(selectAttachmentsByAssetTypeFieldValues)
	const [csvImportInputRef, toggleCsvImportInputRef] = useFileInputRef()
	const [showIssueCommandMenu, openIssueCommandMenu, closeIssueCommandMenu] = useOpenState(false)
	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const sortedProcedureInitiatives = useSort(procedureInitiatives, submittedAtModelSortFn)

	const assetTypeFieldSchema = useDeserializeFieldSchema(latestAssetTypeFields?.fields ?? EMPTY_ARRAY)

	const assetDataFilterSchema = useAssetDataFilterSchema({
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
	})

	const assetDataFilters = useMemo(() => {
		return assetDataFilterSchema.deserializeFilters(serializedAssetDataFilters)
	}, [assetDataFilterSchema, serializedAssetDataFilters])

	const handleAssetDataFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(
				setAssetTypeFilters({
					id: assetType.uuid,
					filters: assetDataFilterSchema.serializeFilters(filters),
				}),
			)
		},
		[assetType.uuid, dispatch, assetDataFilterSchema],
	)

	const handleColumnVisibilityChange = useCallback(
		(updater: Updater<VisibilityState>) => {
			dispatch(
				setAssetTypeColumnVisibility({
					id: assetType.uuid,
					columnVisibility: typeof updater === "function" ? updater(columnVisibility) : updater,
				}),
			)
		},
		[assetType.uuid, dispatch, columnVisibility],
	)

	const handleSortingChange = useCallback(
		(updater: Updater<SortingState>) => {
			dispatch(
				setAssetTypeColumnSorting({
					id: assetType.uuid,
					columnSorting: typeof updater === "function" ? updater(columnSorting) : updater,
				}),
			)
		},
		[assetType.uuid, dispatch, columnSorting],
	)

	const assetDataFilterManager = useFilterManager({
		filterSchema: assetDataFilterSchema,
		filters: assetDataFilters,
		onFilterChange: handleAssetDataFiltersChange,
	})

	const filteredAssetData = useFilters({
		filterManager: assetDataFilterManager,
		filters: assetDataFilters,
		data: assetData,
	})

	const assetTableColumns = useAssetTableColumns({ assetTypeIdentifiers, assetTypeFieldSchema, assetTypeStatuses })

	const assetTable = useAssetTable({
		columns: assetTableColumns,
		data: filteredAssetData,
		enableRowSelection: true,
		initialState: {
			pagination: {
				pageSize: defaultRowsPerPageOptions[0],
			},
		},
		state: {
			columnVisibility: columnVisibility,
			sorting: columnSorting,
		},
		onColumnVisibilityChange: handleColumnVisibilityChange,
		onSortingChange: handleSortingChange,
	})

	const handleExportToPdf = useCallback(
		(options: AssetPdfExportOptions) => {
			const selectedRows = assetTable.getSelectedRowModel().rows.map((row) => row.original.asset.uuid)

			void sdk.assets
				.getAssetsPdfReport(assetType.uuid, selectedRows, options)
				.then(() => {
					toast.success({
						title: "Report requested",
						description: `Requested report for ${selectedRows.length} ${pluralize("asset", selectedRows.length)}. Visit the Reports page when it is ready.`,
					})
				})
				.catch((error: unknown) => {
					toast.error({
						title: "Failed to generate report",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
		},
		[assetTable, assetType.uuid],
	)

	const importFromCSV = useCallback<ChangeEventHandler<HTMLInputElement>>(
		(e) => {
			const { files } = e.target

			if (!files || files.length === 0) return

			const file = files[0]!

			const assetDataMap = new Map(assetData.map((assetData) => [assetData.asset.uuid, assetData]))

			Papa.parse<Record<string, string>>(file, {
				header: true,
				complete: async (results) => {
					const { data: datas } = results

					const csvImporter = new CSVImporter({
						data: datas,
						columns: {
							uuid: {
								header: "uuid",
								required: true,
								transformValue: (value) => value,
							},
							label: {
								header: "Label",
								required: false,
								transformValue: (value) => value,
							},
							assetTypeFieldValues: {
								columns: getCSVImportColumnDefsOfFieldSchema(assetTypeFieldSchema),
							},
							assetTypeIdentifierValues: {
								columns: getCSVImportColumnDefsOfIdentifiers(assetTypeIdentifiers),
							},
						},
					})

					try {
						csvImporter.validate()
						const validatedCsvData = csvImporter.validatedData

						const labelByAsset: Record<Asset["uuid"], Asset["label"]> = {}
						const identifierValuesByAsset: Record<
							Asset["uuid"],
							Record<AssetTypeIdentifier["uuid"], AssetTypeIdentifierValue["value"]>
						> = {}
						const fieldValuesByAsset: Record<Asset["uuid"], FieldValues> = {}
						const fieldValuesAttachmentsByFieldIdByAsset: Record<
							Asset["uuid"],
							Record<Field["id"], Stored<AssetTypeFieldValuesAttachment>[]>
						> = {}

						for (const {
							uuid,
							label,
							assetTypeFieldValues: values,
							assetTypeIdentifierValues: identifierValues,
						} of validatedCsvData) {
							if (!assetDataMap.has(uuid)) {
								throw new Error(`Asset with uuid ${uuid} does not exist.`)
							}

							const { asset, assetTypeFieldValues, assetTypeIdentifierValues } = assetDataMap.get(uuid)!

							// Label
							if (label !== asset.label) {
								labelByAsset[uuid] = label
							}

							// Identifier Values
							if (Object.keys(identifierValues).length !== 0) {
								identifierValuesByAsset[uuid] = {}

								for (const [assetTypeIdentifierId, value] of Object.entries(identifierValues)) {
									const previousIdentifierValue = assetTypeIdentifierValues[assetTypeIdentifierId]

									if (previousIdentifierValue?.value === value) continue

									identifierValuesByAsset[uuid][assetTypeIdentifierId] = value
								}
							}

							// Field Values
							if (Object.keys(values).length !== 0) {
								if (!assetTypeFieldSchema.areValuesValid(values)) {
									throw new Error("Field value validation failed.")
								}

								const attachments =
									attachmentsByAssetTypeFieldValues[assetTypeFieldValues?.uuid ?? ""] ?? []

								const previousFieldValues = assetTypeFieldSchema.initializeValues(
									assetTypeFieldSchema.deserializeValues(assetTypeFieldValues?.values ?? {}),
								)

								const updatedFieldValues = {
									...previousFieldValues,
									...values,
								}

								if (assetTypeFieldSchema.areValuesEqual(previousFieldValues, updatedFieldValues))
									continue

								fieldValuesAttachmentsByFieldIdByAsset[uuid] =
									getFieldValuesAttachmentsByFieldId(attachments)

								fieldValuesByAsset[uuid] = updatedFieldValues
							}
						}

						const loadingToastId = loadingToast({
							id: uuid(),
							description: `Importing ${file.name}`,
						})

						const promises: Promise<unknown>[] = []

						// Label
						const [_offlineUpdatedAssets, assetUpdatePromises] = sdk.assets.bulkUpdate(
							Object.entries(labelByAsset).map(([assetId, label]) => ({
								uuid: assetId,
								label: label,
							})),
						)

						promises.push(...assetUpdatePromises)

						// Identifier Values
						const [_offlineAssetTypeIdentifierValues, assetTypeIdentifierValuePromises] =
							sdk.assetTypeIdentifierValues.bulkAdd(
								Object.entries(identifierValuesByAsset).flatMap(([assetId, valuesByIdentifier]) => {
									return Object.entries(valuesByIdentifier).map(([assetTypeIdentiferId, value]) => {
										return {
											asset: assetId,
											asset_type_identifier: assetTypeIdentiferId,
											value: value,
										}
									})
								}),
							)

						promises.push(assetTypeIdentifierValuePromises)

						// Field Values
						const [offlineAssetTypeFieldValues, batchPromises] = sdk.assetTypeFieldValues.bulkAdd({
							values: {},
							payloads: Object.entries(fieldValuesByAsset).map(([assetId, values]) => ({
								asset: assetId,
								values: assetTypeFieldSchema.serializeValues(values),
								fields_revision: latestAssetTypeFields!.uuid,
								published_at: new Date().toISOString(),
							})),
						})

						promises.push(...batchPromises)

						void sdk.assetTypeFieldValuesAttachments.bulkAdd(
							offlineAssetTypeFieldValues.flatMap(({ uuid, asset }) => {
								const attachmentsByFieldId = fieldValuesAttachmentsByFieldIdByAsset[asset] ?? []
								return Object.entries(attachmentsByFieldId).flatMap(([fieldId, attachments]) =>
									attachments.map((attachment) => ({
										field_values: uuid,
										field_identifier: fieldId,
										file_sha1: attachment.file_sha1,
										file_extension: attachment.file_extension,
										file_name: attachment.file_name,
										file_type: attachment.file_type,
										object_url: attachment.object_url,
									})),
								)
							}),
						)

						void Promise.all(promises)
							.then(() => {
								toast.success({
									title: "Success",
									description: `Imported ${file.name}`,
								})
							})
							.catch((e: unknown) => {
								toast.error({
									title: "CSV import failed",
									description: e instanceof Error ? e.message : ERROR_MSG_UNEXPECTED,
								})
							})
							.finally(() => {
								toast.dismiss(loadingToastId)
							})
					} catch (e: unknown) {
						toast.error({
							title: "CSV import failed",
							description: e instanceof Error ? e.message : ERROR_MSG_UNEXPECTED,
						})
					}
				},
				error: (error) => {
					toast.error({
						title: "CSV import failed",
						description: error.message ?? ERROR_MSG_UNEXPECTED,
					})
				},
			})

			csvImportInputRef.current!.value = ""
		},
		[
			assetTypeFieldSchema,
			csvImportInputRef,
			latestAssetTypeFields?.uuid,
			assetData,
			attachmentsByAssetTypeFieldValues,
			assetTypeIdentifiers,
		],
	)

	const exportToCSV = useCallback(() => {
		const exporter = getAssetDataCsvExporter(
			assetTable.getSortedRowModel().rows.map((row) => row.original),
			{
				assetTypeStatuses: assetTypeStatuses,
				assetTypeIdentifiers: assetTypeIdentifiers,
				assetTypeFieldSchema: assetTypeFieldSchema,
			},
		)
		const csvData = Papa.unparse(exporter.toPapaUnparseFormat(), { quotes: true })
		const blob = new Blob([csvData], { type: "text/csv" })
		saveAs(blob, toFileNameSafeString(assetType.name, ".csv"))
	}, [assetTable, assetType.name, assetTypeFieldSchema, assetTypeIdentifiers, assetTypeStatuses])

	const handleProcedureInitiativeCardClick = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			void navigate({
				from: "/$organizationId/$projectId/asset-types/$assetTypeId/",
				to: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
				params: (prev) => ({
					...prev,
					procedureInitiativeId: procedureInitiative.uuid,
				}),
			})
		},
		[navigate],
	)

	const handleViewProcedureInitiative = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			void navigate({
				from: "/$organizationId/$projectId/asset-types/$assetTypeId/",
				to: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
				params: (prev) => ({ ...prev, procedureInitiativeId: procedureInitiative.uuid }),
			})
		},
		[navigate],
	)

	const handleDeleteProcedureInitiative = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			openConfirmDeleteDialog({
				title: "Delete initiave",
				description: "Are you sure you want to delete this initiave? This action cannot be undone.",
				onAction: () => {
					void sdk.procedureInitiatives.delete(procedureInitiative.uuid)
				},
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleAssetTableRowClick = useCallback(
		(assetData: AssetData) => {
			void navigate({
				from: "/$organizationId/$projectId/asset-types/$assetTypeId/",
				to: "/$organizationId/$projectId/assets/$assetId",
				params: (prev) => ({
					...prev,
					assetId: assetData.asset.uuid,
				}),
			})
		},
		[navigate],
	)

	const selectedRowCount = assetTable.getSelectedRowModel().rows.length

	const getSelectedAssetIds = useCallback(
		() => assetTable.getSelectedRowModel().rows.map((row) => row.original.asset.uuid),
		[assetTable],
	)

	const handleLinkIssues = useCallback(
		(issue: Stored<Issue>) => {
			const assetIds = getSelectedAssetIds()

			const [_, promise] = sdk.issueAssociations.bulkAdd(
				assetIds.map((assetId) => ({
					asset: assetId,
					issue: issue.uuid,
				})),
			)

			void promise.then(() => {
				toast.success({
					title: "Success",
					description: "Successfully linked issue to asset.",
				})
			})
		},
		[getSelectedAssetIds],
	)

	const handleCreateIssueAndLink = useCallback(
		(issueType: Stored<IssueType>) => {
			const [offlineIssue, _promise] = sdk.issues.add({
				title: "",
				description: null,
				assigned_to: null,
				due_date: null,
				issue_type: issueType.uuid,
				priority: issueType.default_priority!,
				status: issueType.default_status!,
				project: projectId,
			})

			handleLinkIssues(offlineIssue)

			void navigate({
				from: "/$organizationId/$projectId/assets/$assetId",
				to: "/$organizationId/$projectId/issues/$issueId",
				params: (prev) => ({ ...prev, issueId: offlineIssue.uuid }),
			})
		},
		[projectId, handleLinkIssues, navigate],
	)

	const handleBulkStatusChange = useCallback(
		(assetTypeStatus: Stored<AssetTypeStatus>) => {
			const assetIds = getSelectedAssetIds()
			sdk.assets.bulkUpdate(assetIds.map((uuid) => ({ uuid, status: assetTypeStatus.uuid })))
		},
		[getSelectedAssetIds],
	)

	const handleStartInitiative = useCallback(
		(procedureType?: Stored<ProcedureType>) => {
			if (!procedureType) return

			const assetIds = getSelectedAssetIds()

			const [offlineProcedureInitiave] = sdk.procedureInitiatives.add({
				procedure_type: procedureType.uuid,
				project: projectId,
				asset_type: assetType.uuid,
			})

			void sdk.procedures.bulkAdd(
				assetIds.map((assetId) => ({
					procedure_type: procedureType.uuid,
					asset: assetId,
					project: projectId,
					issue: null,
					procedure_initiative: offlineProcedureInitiave.uuid,
				})),
			)

			// TODO: Navigate to procedure initiative detail view here?
		},
		[getSelectedAssetIds, projectId, assetType.uuid],
	)

	const handleBulkDelete = useCallback(() => {
		const assetIds = getSelectedAssetIds()

		openConfirmDeleteDialog({
			title: `Delete ${pluralize("asset", assetIds.length)}`,
			description: `The ${assetIds.length} ${pluralize("asset", assetIds.length)} will be deleted. This action cannot be undone.`,
			onAction: () => void sdk.assets.bulkDelete(assetIds),
		})

		assetTable.resetRowSelection()
	}, [openConfirmDeleteDialog, getSelectedAssetIds, assetTable])

	return (
		<ViewContainer>
			<div className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex h-max w-full justify-between gap-2">
					<Heading className="flex min-w-0 items-center gap-2" size="xl">
						<AssetTypeIcon assetTypeId={assetType.uuid} inline={false} />
						<Text className="truncate">{assetType.name}</Text>
					</Heading>

					<AssetTypeDropdownMenu assetType={assetType} align="end" size="sm">
						<IconButton size="sm" variant="ghost" accentColor="base">
							<LuIcon icon={modelIconMapping.more} />
						</IconButton>
					</AssetTypeDropdownMenu>
				</div>

				<Separator size="full" />

				<div className="flex size-full flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<Card className="flex shrink-0 flex-col gap-2" size="sm">
						<div className="flex items-center justify-between">
							<Heading>Initiatives</Heading>

							<Link
								from="/$organizationId/$projectId/asset-types/$assetTypeId/"
								to="/$organizationId/$projectId/initiatives/create"
								search={{ redirect: location.href, asset_type: assetType.uuid }}
							>
								<Button variant="surface" size="sm">
									<LuIcon icon={modelIconMapping.add} />
									Create
								</Button>
							</Link>
						</div>

						<div className="flex w-full shrink-0 scroll-px-4 gap-4 overflow-x-scroll">
							{sortedProcedureInitiatives.map((procedureInitiative) => (
								<ProcedureInitiativeCard
									key={procedureInitiative.uuid}
									variant="surface"
									onClick={handleProcedureInitiativeCardClick}
									onView={handleViewProcedureInitiative}
									onDelete={handleDeleteProcedureInitiative}
									procedureInitiative={procedureInitiative}
									className={cx(interactiveCardCva(), "w-xs h-64 shrink-0 cursor-pointer")}
									size="sm"
								/>
							))}
						</div>
					</Card>

					<Card className="flex flex-col gap-2" size="sm">
						<ButtonGroup
							className="flex w-full justify-between gap-2"
							size="xs"
							variant="soft"
							accentColor="base"
						>
							<div className="flex items-center gap-2">
								<Heading>Assets</Heading>
								<AssetDataFilterMenu
									filterManager={assetDataFilterManager}
									assetTypeFieldSchema={assetTypeFieldSchema}
									assetTypeIdentifiers={assetTypeIdentifiers}
									assetTypeStatuses={assetTypeStatuses}
									align="start"
									size="sm"
								>
									<Button>
										<LuIcon icon={modelIconMapping.filter} />
										Filter
									</Button>
								</AssetDataFilterMenu>

								{assetDataFilterManager.getFilters().length > 0 && (
									<IconButton onClick={assetDataFilterManager.removeAll}>
										<LuIcon icon={modelIconMapping.close} />
									</IconButton>
								)}

								{/* TODO: Conditionally hide if there is no supported things to import */}
								<Button onClick={toggleCsvImportInputRef}>
									<LuIcon icon={modelIconMapping.upload} />
									Import
								</Button>
							</div>

							<div className="flex items-center gap-2">
								{selectedRowCount > 0 ? (
									<>
										<AssetBulkDropdownMenu
											onExportPdf={openPdfExportOptionsDialog}
											onExportCsv={exportToCSV}
											onLinkIssue={openIssueCommandMenu}
											onDelete={handleBulkDelete}
											statuses={assetTypeStatuses}
											onStatusSelect={handleBulkStatusChange}
											procedureTypes={procedureTypes}
											onProcedureTypeSelect={handleStartInitiative}
											size="sm"
										>
											<Button>
												<LuIcon icon={modelIconMapping.command} />
												Actions
											</Button>
										</AssetBulkDropdownMenu>
									</>
								) : null}

								<ColumnVisibilityDropdown table={{ ...assetTable }} align="end" size="sm">
									<IconButton>
										<LuIcon icon={tableIconMapping.columns} />
									</IconButton>
								</ColumnVisibilityDropdown>
							</div>
						</ButtonGroup>

						<FilterRenderer filterManager={assetDataFilterManager} size="sm" />

						<div className="max-h-full w-full overflow-auto">
							<Table
								className="w-full"
								variant="surface"
								size="sm"
								table={{ ...assetTable }}
								onRowClick={handleAssetTableRowClick}
								compact
								interactive
								emptyText="No assets."
							/>
						</div>

						<PaginationControl<AssetData> table={{ ...assetTable }} />
					</Card>
				</div>
			</div>
			<input
				className="hidden"
				ref={csvImportInputRef}
				type="file"
				accept=".csv"
				onChange={importFromCSV}
				onClick={stopPropagation}
			/>

			<AssetPdfExportOptionsDialog
				key={assetType.uuid}
				assetTypeId={assetType.uuid}
				onSubmitOptions={handleExportToPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>

			<IssueCommandMenu
				issuesAndIdentifiers={issuesAndIdentifiers}
				issueTypes={issueTypes}
				onIssueSelect={handleLinkIssues}
				onCreateIssue={handleCreateIssueAndLink}
				open={showIssueCommandMenu}
				onOpenChange={closeIssueCommandMenu}
			/>
		</ViewContainer>
	)
})
AssetTypeDetailView.displayName = "AssetTypeDetailView"
